import { HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable, throwError, of, timer, BehaviorSubject, EMPTY } from 'rxjs';
import { SecurityHelper } from '../pages/common/securityHelper';
import { catchError, map, mergeMap, tap, retryWhen, switchMap, filter, take, delay } from 'rxjs/operators';
import { Helper } from '../pages/common/helper';
import { LoginService } from '../pages/login/login.service';
import { Router } from '@angular/router';
import { OnlineOfflineService } from '../services/online-offline.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  retryDelay = 1000;
  retryMaxAttempts = 2;

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private loginService: LoginService,
    private onlineOfflineService: OnlineOfflineService,
    private router: Router,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (!navigator.onLine) {

      console.warn('You are offline. Request blocked:', request.url);
      return EMPTY;
    }
    else {

      if (request.url.indexOf('googleapis.com') < 0) {

        request = this.addToken(request);
      }

    }

    // handle request
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 412) {
          // Handle 401 error (Unauthorized)
          return this.handle401Error(request, next);
        } else {
          // For other errors, retry after a delay
          return this.retryAfterDelay(request, next, error);
        }
      })
    );

  }

  // handle request error with retry after delay
  private retryAfterDelay(request: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retryWhen(errors =>
        errors.pipe(
          mergeMap((response: HttpErrorResponse, count: number) => {

            // Get url
            const splitUrl = request.url.split('IFSService/');
            const strUrl = splitUrl.length > 1 ? splitUrl[1] : splitUrl[0];

            // No need to retry, handle this error inside handle401Error()
            if (response.status === 401) {
              return throwError(() => error);
            }

            // No need to retry, handle this error inside handle401Error()
            if (response && response.status == 412) {
              return throwError(() => error);
            }

            // Forbidden
            if (response && response.status == 403) {
              const customError = {
                'name': 'Forbidden',
                'message': 'You do not have permission to access this resource.'
              }
              return throwError(() => customError);
            }

            // Bad Request
            if (response && response.status === 400) {

              // Build error message
              let errorTitle = "Bad Request";
              if (response.error) {
                if (response.error.title)
                  errorTitle = response.error.title;
              }

              const errorList = this.extractErrorMessagesFromErrorResponse(response);
              const errorMessage = (errorList.length > 0) ? errorList.toString() : "Invalid data";

              const customError = {
                'name': errorTitle,
                'message': `Unable to send request to ${strUrl} | ERROR: ${errorMessage}<br><br>Please make sure data are correct. Please contact your system administrator if problem persists.`
              }

              return throwError(() => customError);

            }

            // Not found
            if (response && response.status == 404) {

              const customError = {
                'name': 'Page not found',
                'message': `The page you are trying to access in not found.`
              }

              return throwError(() => customError);
            }

            // Service is down
            if (response && response.status == 503) {

              const customError = {
                'name': 'Service is unavailable',
                'message': 'We are currently undergoing a release. Please try again later.'
              }

              return throwError(() => customError);
            }

            // Retry only if error is not 401 and count is within max retries
            if (count >= this.retryMaxAttempts) {
              return throwError(() => new Error(`Max retry attempts reached: ${response.message}`));
            }

            // allow the client to retry request twice if status is 0
            return of(response).pipe(
              tap(error => console.log(`Retry count ${count + 1} || ${new Date()} || Retrying ${error.url}.`)),
              mergeMap(() => {
                // apply exponential backoff (1st try = 2000 ms, 2nd try = 4000ms ...)
                const computeDelay = (this.retryDelay * ((count + 1) * 2));
                return timer(computeDelay)
              })
            );
          })
        )
      )
    );
  }

  private extractErrorMessagesFromErrorResponse(errorResponse: HttpErrorResponse): any[] {
    // 1 - Create empty array to store errors
    const errors = [];

    // 2 - check if the error object is present in the response
    if (errorResponse.error) {

      // 3 - Push the main error message to the array of errors
      if (errorResponse.error.message) {
        if (errorResponse.error.message.toString() !== 'undefined')
          errors.push(errorResponse.error.message.split('|')[0].trim());
      }

      // 4 - Check for Laravel form validation error messages object
      if (errorResponse.error.errors) {

        // 5 - For each error property (which is a form field)
        for (const property in errorResponse.error.errors) {

          if (errorResponse.error.errors.hasOwnProperty(property)) {

            // 6 - Extract it's array of errors
            const propertyErrors: Array<string> = errorResponse.error.errors[property];

            // 7 - Push all errors in the array to the errors array
            propertyErrors.forEach(error => {
              if (error) {
                if (error.toString() !== 'undefined')
                  errors.push(error.split('|')[0].trim())
              }
            });
          }

        }

      }

    }

    return errors;
  };

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.loginService.refreshToken().pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(res.entity);
          // Retry the original request with the new token
          return next.handle(this.addToken(req));
        }),
        catchError((refreshError) => {
          this.isRefreshing = false;
          SecurityHelper.SetHTTPRequestsUnathorized(true);
          return throwError(() => refreshError);
        })
      );
    } else {
      // Wait until the refresh token process is complete
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((newToken: any) => {
          // Retry the original request with the new token
          return next.handle(this.addToken(req));
        }),
        catchError((refreshError: HttpErrorResponse) => {
          this.isRefreshing = false;
          // clear storage
          //SecurityHelper.WebStorage.ClearStorageData.All();
          // go to login page
          //this.router.navigate(['../']);
          // (refreshError.status == 401) ? 'Session expired' : 'Unauthorized access'
          SecurityHelper.SetHTTPRequestsUnathorized(true);
          const customError = {
            'name': 'Unauthorized access',
            'message': 'Please login again to resolve the issue.'
          }
          return throwError(() => customError);
        })
      );
    }
  }

  private addToken(req: HttpRequest<any>): HttpRequest<any> {
    // Set to true to allow Cookies to be sent or received
    return req.clone({ withCredentials: true });
  }

}
